import React from 'react';
import { Car, MessageCircle, MapPin, Globe, Plane, Shield, Clock } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const FAQ = () => {
  const faqs = [
    {
      question: "¿Qué documentos necesito para alquilar un vehículo?",
      answer: "Necesitas: licencia de conducir vigente (nacional o internacional), identificación válida (pasaporte o residencia), y una tarjeta de crédito o débito para el depósito de seguridad de $100 USD."
    },
    {
      question: "¿Puedo recoger el vehículo en el aeropuerto?",
      answer: "Sí, ofrecemos entrega gratuita en el Aeropuerto Internacional Las Américas (SDQ) en Santo Domingo. También entregamos en el Aeropuerto de Punta Cana. Para otros aeropuertos, contáctanos para verificar disponibilidad y costos."
    },
    {
      question: "¿Cuál es la edad mínima para alquilar?",
      answer: "La edad mínima es 18 años con licencia de conducir vigente. No cobramos cargos adicionales por conductores jóvenes."
    },
    {
      question: "¿El seguro está incluido en el precio?",
      answer: "Sí, todos nuestros vehículos incluyen seguro de responsabilidad civil de ley en República Dominicana y asistencia 24/7. No hay costos ocultos."
    },
    {
      question: "¿Puedo devolver el vehículo en un lugar diferente?",
      answer: "Sí, ofrecemos devolución en diferentes ubicaciones. Puede aplicar un cargo adicional dependiendo de la distancia. Contáctanos para más detalles."
    },
    {
      question: "¿Qué pasa si tengo un problema con el vehículo?",
      answer: "Tenemos asistencia 24/7 vía WhatsApp (+1 809-964-0099). Nuestro equipo te ayudará inmediatamente con cualquier problema mecánico o emergencia."
    },
    {
      question: "¿Cómo funciona el combustible?",
      answer: "El vehículo se entrega con combustible. Debes devolverlo con la misma cantidad de combustible con la que lo recibiste."
    },
    {
      question: "¿Puedo cancelar mi reserva?",
      answer: "Las reservas confirmadas no son reembolsables. Al reservar un vehículo para una fecha específica, rechazamos otras solicitudes de clientes para ese mismo período. Por este motivo, el depósito de reserva no será devuelto en caso de cancelación."
    },
    {
      question: "¿Qué métodos de pago aceptan?",
      answer: "Aceptamos: efectivo (USD y pesos dominicanos), tarjetas de crédito/débito, transferencias bancarias (Caribe Express, Western Union, Popular, BanReservas, BBVA, Zelle, Bizum) y PayPal."
    },
    {
      question: "¿Hay descuentos por alquileres largos?",
      answer: "Sí, ofrecemos 7% de descuento en alquileres de 6 días o más. Contáctanos por WhatsApp para aplicar tu descuento."
    },
    {
      question: "¿Puedo cruzar a Haití con el vehículo?",
      answer: "No, nuestros vehículos no están autorizados para salir de República Dominicana. El contrato cubre únicamente territorio dominicano."
    },
    {
      question: "¿Incluyen peajes y multas?",
      answer: "Los peajes y multas de tránsito son responsabilidad del conductor. Te recomendamos respetar los límites de velocidad y las señales de tránsito."
    }
  ];

  // Steps for How It Works
  const steps = [
    {
      number: "01",
      icon: Car,
      title: "Elige tu Vehículo",
      description: "Explora nuestra flota y selecciona el vehículo perfecto para tu viaje."
    },
    {
      number: "02",
      icon: MessageCircle,
      title: "Escríbenos por WhatsApp",
      description: "Contacta con nosotros por WhatsApp (+1 809-964-0099) y confirma tu reserva en minutos."
    },
    {
      number: "03",
      icon: MapPin,
      title: "Recibe el Vehículo",
      description: "Te entregamos el vehículo en el aeropuerto, hotel o cualquier ubicación."
    }
  ];

  // Benefits for tourists
  const touristBenefits = [
    { icon: Globe, title: "Sin Complicaciones", description: "Proceso simplificado para turistas internacionales" },
    { icon: Plane, title: "Recogida en Aeropuerto", description: "Te recibimos y entregamos el vehículo de inmediato" },
    { icon: Shield, title: "Seguro Incluido", description: "Todos nuestros vehículos cuentan con seguro completo" },
    { icon: Clock, title: "Soporte 24/7", description: "Atención en español e inglés durante tu estadía" }
  ];

  // Why Choose Us features
  const whyChooseUsFeatures = [
    { icon: Plane, title: "Entrega en Aeropuerto", description: "Te esperamos en el aeropuerto con tu vehículo listo" },
    { icon: Clock, title: "Atención 24/7", description: "Servicio disponible las 24 horas, todos los días" },
    { icon: Car, title: "Vehículos Modernos", description: "Flota renovada con modelos recientes y limpios" },
    { icon: MessageCircle, title: "Reserva por WhatsApp", description: "Proceso rápido sin formularios complicados" },
    { icon: MapPin, title: "Entrega en Hoteles", description: "Llevamos el vehículo a tu hotel o Airbnb" },
    { icon: Shield, title: "Servicio Confiable", description: "Seguro incluido y transparencia en precios" }
  ];

  return (
    <section id="faq" className="py-20 md:py-32 bg-black">
      <div className="max-w-[1400px] mx-auto px-5 md:px-12 lg:px-20">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-yellow-700/20 border border-yellow-600/30 px-4 py-2 mb-6">
            <span className="text-yellow-600 text-sm font-medium">PREGUNTAS FRECUENTES</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            ¿Tienes Dudas?
          </h2>
          <p className="text-xl text-white/70 max-w-2xl mx-auto">
            Encuentra respuestas a las preguntas más comunes
          </p>
        </div>

        {/* FAQ Accordion */}
        <div className="max-w-4xl mx-auto">
          <Accordion type="single" collapsible className="space-y-4">
            {/* How It Works - Collapsible */}
            <AccordionItem 
              value="how-it-works"
              className="bg-zinc-900 border border-white/10 px-6 data-[state=open]:border-yellow-600/50"
            >
              <AccordionTrigger className="text-white hover:text-yellow-600 text-left text-lg font-semibold py-6 hover:no-underline">
                ¿Cómo funciona el proceso de alquiler?
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <div key={index} className="text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-yellow-700 text-white text-xl font-bold mb-4">
                          {step.number}
                        </div>
                        <div className="flex justify-center mb-3">
                          <Icon className="w-6 h-6 text-yellow-600" />
                        </div>
                        <h4 className="text-white font-semibold mb-2">{step.title}</h4>
                        <p className="text-white/70 text-sm">{step.description}</p>
                      </div>
                    );
                  })}
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Why Choose Us - Collapsible */}
            <AccordionItem 
              value="why-choose-us"
              className="bg-zinc-900 border border-white/10 px-6 data-[state=open]:border-yellow-600/50"
            >
              <AccordionTrigger className="text-white hover:text-yellow-600 text-left text-lg font-semibold py-6 hover:no-underline">
                ¿Por qué elegir La Rent Car de la Isla?
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {whyChooseUsFeatures.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="flex items-start gap-3 bg-black/50 p-4 border border-white/10">
                        <div className="w-10 h-10 bg-yellow-700/20 border border-yellow-600/30 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{feature.title}</h4>
                          <p className="text-white/70 text-sm">{feature.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center mt-6 pt-4 border-t border-white/10">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">+1,000</div>
                    <div className="text-white/70 text-sm">turistas confían en nosotros</div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Perfect for Tourists - Collapsible */}
            <AccordionItem 
              value="for-tourists"
              className="bg-zinc-900 border border-white/10 px-6 data-[state=open]:border-yellow-600/50"
            >
              <AccordionTrigger className="text-white hover:text-yellow-600 text-left text-lg font-semibold py-6 hover:no-underline">
                ¿Por qué es perfecto para turistas?
              </AccordionTrigger>
              <AccordionContent className="pb-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {touristBenefits.map((benefit, index) => {
                    const Icon = benefit.icon;
                    return (
                      <div key={index} className="flex items-start gap-3 bg-black/50 p-4 border border-white/10">
                        <div className="w-10 h-10 bg-yellow-700/20 border border-yellow-600/30 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-yellow-600" />
                        </div>
                        <div>
                          <h4 className="text-white font-semibold mb-1">{benefit.title}</h4>
                          <p className="text-white/70 text-sm">{benefit.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-center gap-6 mt-6 pt-4 border-t border-white/10">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">24/7</div>
                    <div className="text-white/70 text-sm">Soporte</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">+30</div>
                    <div className="text-white/70 text-sm">Países</div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            {/* Regular FAQs */}
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={index} 
                value={`item-${index}`}
                className="bg-zinc-900 border border-white/10 px-6 data-[state=open]:border-yellow-600/50"
              >
                <AccordionTrigger className="text-white hover:text-yellow-600 text-left text-lg font-semibold py-6 hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-white/80 text-base leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <p className="text-white/70 mb-6 text-lg">
            ¿No encuentras la respuesta que buscas?
          </p>
          <a
            href="https://wa.me/18099640099?text=Hola,%20tengo%20una%20pregunta%20sobre%20el%20alquiler"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-yellow-700 text-white px-8 py-4 font-semibold text-lg hover:bg-yellow-800 transition-all duration-300"
          >
            Contáctanos por WhatsApp
          </a>
          
          {/* Cancellation Policy - Small Print */}
          <p className="text-white/40 text-xs text-center max-w-3xl mx-auto leading-relaxed mt-8">
            <span className="font-semibold">Política de cancelación:</span> Las reservas confirmadas no son reembolsables. Al confirmar una reserva, el vehículo queda bloqueado para la fecha seleccionada, lo que implica rechazar otras solicitudes de clientes interesados en el mismo vehículo y período. Por este motivo, el depósito de reserva no será devuelto bajo ninguna circunstancia en caso de cancelación por parte del cliente.
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
